import { type FC, useState } from "react";
import {
  Toolbar,
  Checkbox,
  Button,
  Typography,
  Divider,
  Menu,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  ActionMenuItem,
  ToolbarProgress,
  useConfirmAction,
} from "@/shared/components";
import { useTranslation } from "react-i18next";
import {
  useUpdatePlatformCustomCoversMutation,
  useDeletePlatformCustomCoversMutation,
  type PlatformCustomCover,
} from "@/partner/entities/platforms";

const DividerVertical = (
  <Divider
    orientation="vertical"
    light
    variant="middle"
    style={{ height: "2em", marginLeft: 10, marginRight: 10 }}
  />
);

interface CustomCoversListToolbarProps {
  indeterminate: boolean;
  numSelected: number;
  nodes: PlatformCustomCover[];
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedItems: { id: string }[];
  onCloseSelected: () => void;
}

export const CustomCoversListToolbar: FC<CustomCoversListToolbarProps> = ({
  indeterminate,
  numSelected,
  nodes,
  onSelectAllClick,
  selectedItems,
  onCloseSelected,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [updatePlatformCustomCovers, { loading: updateLoading }] =
    useUpdatePlatformCustomCoversMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("platformCustomCovers.snackbar.bulkUpdated"), {
          variant: "success",
        });
      },
      onError: (error: { message: string }) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["PlatformCustomCovers"],
    });

  const [deletePlatformCustomCovers, { loading: deleteLoading }] =
    useDeletePlatformCustomCoversMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("platformCustomCovers.snackbar.bulkDeleted"), {
          variant: "success",
        });
      },
      onError: (error: { message: string }) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["PlatformCustomCovers"],
    });

  const onPublishSelected = () => {
    updatePlatformCustomCovers({
      variables: {
        ids: selectedItems.map((item) => item.id),
        input: { publishAt: new Date().toISOString() },
      },
    });
  };

  const onUnpublishSelected = () => {
    updatePlatformCustomCovers({
      variables: {
        ids: selectedItems.map((item) => item.id),
        input: { publishAt: null },
      },
    });
  };

  const onDeleteSelected = () => {
    // До этой правки массовое удаление здесь шло вообще без вопроса.
    confirm({
      title: t("common.actions.delete"),
      text: t("common.toolbar.deleteConfirm"),
      onConfirm: () =>
        deletePlatformCustomCovers({
          variables: {
            ids: selectedItems.map((item) => item.id),
          },
        }),
    });
  };

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={numSelected === nodes.length}
        onChange={onSelectAllClick}
        sx={{
          marginLeft: "-16px",
          marginRight: {
            xs: 0,
            sm: "16px",
          },
        }}
      />

      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>

      {DividerVertical}

      <Button
        color="inherit"
        onClick={handleClick}
        disabled={numSelected === 0}
      >
        {t("common.actions.actions")}
      </Button>

      <Menu
        id="custom-covers-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onPublishSelected();
            handleClose();
          }}
        >
          {t("common.actions.publish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onUnpublishSelected();
            handleClose();
          }}
        >
          {t("common.actions.unpublish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={deleteLoading}
          onClick={() => {
            onDeleteSelected();
            handleClose();
          }}
        >
          {t("common.actions.delete")}
        </ActionMenuItem>
      </Menu>

      <ToolbarProgress loading={updateLoading || deleteLoading} />
      {confirmDialog}
    </Toolbar>
  );
};
